import { connect } from 'react-redux';
import { debounce, get } from 'lodash';

// constants
import { cropIds } from 'constants/crops';
import { selectIdPrefix } from 'constants/values';

// actions
import { updatePlantingPlanParams } from 'planting-plan-form';
import { searchSeedProductsFromAutocompleteSelect } from 'current/uiwidgets/';
import { loadNewAdvancedScriptIfChangesScenario } from 'scripts';

// selectors
import {
  getAvailableYieldLayersSummaryForCrop,
  isReviewScriptStep
} from 'advanced/selectors';
import { getNumZonesInActiveScenario } from 'advanced/scenarios/selectors';
import {
  canUpdatePlantingPlan,
  getPlantingPlanMaxDate,
  getPlantingPlanMinDate,
  getPlantingPlanParams
} from 'planting-plan-form/selectors';
import { getPlantingPlanParamsUiValidationResult } from 'validations/selectors';
import { canViewYieldData } from 'product-features/selectors';
import { getSeedProductInputBySelectId, getActiveFieldCountryCode } from 'current/selectors';
import { 
  getAccountCountry, 
  isSingleZoneRecommendationEnabled,
  getBagSizeForCrop
} from 'user/selectors';
import { getCurrency } from 'user/userSelectors';
import { isAppMode as isAppModeFunc } from 'settings/selectors';

// components
import AdvancedPlantingPlanForm from './AdvancedPlantingPlanForm';
import { convertPlantingPlanParamsToUpdate } from '../util/parse';
import moment from 'moment';

// Debounce helps case where user wants to change multiple fields without being interrupted by an update mid-edit.
const onUpdatePlantingPlanDebounced = debounce(
  loadNewAdvancedScriptIfChangesScenario,
  1000
); // (milliseconds)
const onUpdatePlantingPlan = () => (dispatch, getState) =>
  isReviewScriptStep(getState())
  && onUpdatePlantingPlanDebounced(dispatch, getState);

// Force date to be passed in so it isn't considered a date change
// TODO Look into onUpdatePlantingPlanParams and its use of findActivity to find a more obvious long term fix
const updatePlantingPlanParamsKeepDate
  = (passThrough) => (dispatch, getState) =>
    updatePlantingPlanParams({
      date: getPlantingPlanParams(getState()).date,
      ...passThrough
    })(dispatch, getState);

export default connect(
  (state) => {
    const seedProductInput = getSeedProductInputBySelectId(
      state,
      `${selectIdPrefix}1`
    );

    return {
      accountCountry: getAccountCountry(state),
      bagSize: getBagSizeForCrop(state, 'corn'), // hard-coded to corn for now
      numZonesInActiveScenario: getNumZonesInActiveScenario(state),
      canUpdatePlantingPlan: canUpdatePlantingPlan(state),
      canViewYieldData: canViewYieldData(state),
      currency: getCurrency(state),
      isAppMode: isAppModeFunc(state),
      maxDate: getPlantingPlanMaxDate(state),
      maxZoneCount: 12,
      minDate: getPlantingPlanMinDate(state),
      minZoneCount: 3,
      seedProducts: get(seedProductInput, `products`, []), // Undefined until user enters search fragment.
      isReviewScriptStep: isReviewScriptStep(state),
      plantingPlanParams: getPlantingPlanParams(state),
      yieldMapsHistoricalData: getAvailableYieldLayersSummaryForCrop(
        state,
        cropIds.CORN
      ), // Hard-coded to corn for now.
      validationResult: getPlantingPlanParamsUiValidationResult(state),
      fieldCountry: getActiveFieldCountryCode(state),
      isSingleZoneRecommendationEnabled: isSingleZoneRecommendationEnabled(state),
    };
  },
  (dispatch) => {
    return {
      // These planting plan update functions only called from Enter Planting Plan Screen.
      onChangeHybridSearchQuery: debounce(({ fragment, receiveOptions }) => {
        dispatch(
          searchSeedProductsFromAutocompleteSelect({
            fragment,
            advancedOnly: true,
            selectId: `${selectIdPrefix}1`,
            receiveOptions
          })
        );
      }, 200), // (milliseconds) - want largest possible value that doesn't add delay noticeable to user.
      onSelectDate: (date) => {
        dispatch(
          updatePlantingPlanParams({
            date:
              moment(date).format('YYYY-MM-DD')
              + moment(date).format('THH:mm:ss.SSS')
              + 'Z'
          })
        );
      },
      onSelectProduct: ({ productId }) => {
        dispatch(updatePlantingPlanParamsKeepDate({ productId }));
      },
      onSelectSeason: (_, seasonId) => {
        dispatch(updatePlantingPlanParamsKeepDate({ agronomicSeason: seasonId }));
      },
      // These planting plan update functions called from Review Scenario screen.
      /* TODO along with next significant update to this logic - separate the "immediate" and "debounced" code more
         cleanly so that it is not possible for the debounced code to execute at a time when data is not available.
         Currently, some code runs in onUpdatePlantingPlan() debounced 1 second later and sometimes the activityUUID
         is not yet available from parallel execution un updatePlantingPlanParamsKeepDate(). (inter-leaved dispatches
          between both calls creates a multi-thread-style race condition.) */
      onChangeGoalRatio: (e) => {
        dispatch(
          updatePlantingPlanParamsKeepDate({ 'goal-ratio': e.target.value })
        );
        dispatch(onUpdatePlantingPlan());
      },
      onChangeNumZones: (_, id) => {
        dispatch(updatePlantingPlanParamsKeepDate({ 'num-zones': id }));
        dispatch(onUpdatePlantingPlan());
      },
      onChangePriceCommodity: (priceCommodity) => {
        const price = convertPlantingPlanParamsToUpdate(priceCommodity);
        dispatch(
          updatePlantingPlanParamsKeepDate({ 'price-commodity': price })
        );
        dispatch(onUpdatePlantingPlan());
      },
      onChangePriceProduct: (priceProduct) => {
        const price = convertPlantingPlanParamsToUpdate(priceProduct);
        dispatch(updatePlantingPlanParamsKeepDate({ 'price-product': price }));
        dispatch(onUpdatePlantingPlan());
      },
      onChangeTargetYield: (targetYield) => {
        const target = convertPlantingPlanParamsToUpdate(targetYield);
        dispatch(updatePlantingPlanParamsKeepDate({ 'target-yield': target }));
        dispatch(onUpdatePlantingPlan());
      },
      onChangeCurrentAvgPopulation: (curentAvgPopulation) => {
        const population = convertPlantingPlanParamsToUpdate(curentAvgPopulation);
        dispatch(updatePlantingPlanParamsKeepDate({ currentAveragePopulation: population }));
        dispatch(onUpdatePlantingPlan());
      }
    };
  }
)(AdvancedPlantingPlanForm);
